import { createContext, useContext, useState, useEffect } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const CACHE_KEY = "tactis_user";

// Persist only non-sensitive display fields.
const slim = (u) => (u ? { id: u.id, email: u.email, full_name: u.full_name, role: u.role } : null);

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readCache);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the session check on public pages with no prior session hint (avoids benign 401 noise).
    if (!readCache()) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => { const s = slim(r.data); setUser(s); localStorage.setItem(CACHE_KEY, JSON.stringify(s)); })
      .catch(() => { localStorage.removeItem(CACHE_KEY); setUser(null); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called after MFA verify / Google login — the backend has already set the session cookie.
  const login = (userData) => {
    const s = slim(userData);
    localStorage.setItem(CACHE_KEY, JSON.stringify(s));
    setUser(s);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(CACHE_KEY);
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
